import type { BulkEvent } from '@Common/types/analytics/bulk_events';

import { getDeviceId } from '@sh4res/bo-common/utils/device_id';
import { isLocalEnv } from '@sh4res/bo-common/utils/env';

import { getStoredEvents, removeStoredEvents } from '@Utils/analytics/storage';
import { submitAnalyticsEvents } from '@Utils/analytics/submitAnalyticsEvents';

const BATCHING_INTERVAL = 60_000;
const BATCH_SIZE = 50;
const MAX_RETRIES = 6;

/**
 * On local envs the backend logs to the console instead of OpenSearch, and a dev server reloads
 * constantly, so uploading is off by default. Set this key to 'true' in localStorage to check
 * the pipeline end to end locally.
 */
const LOCAL_OVERRIDE_KEY = 'log-analytics-events';

const shouldUpload = (): boolean => {
  if (!isLocalEnv()) {
    return true;
  }

  return localStorage.getItem(LOCAL_OVERRIDE_KEY) === 'true';
};

const chunk = (events: BulkEvent[]): BulkEvent[][] => {
  const batches: BulkEvent[][] = [];

  for (let index = 0; index < events.length; index += BATCH_SIZE) {
    batches.push(events.slice(index, index + BATCH_SIZE));
  }

  return batches;
};

const delay = async (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const sendBatch = async (batch: BulkEvent[], keepalive: boolean): Promise<boolean> =>
  submitAnalyticsEvents(
    {
      deviceTime: new Date().toISOString(),
      clockCorrection: undefined,
      staticDeviceInfo: { deviceId: getDeviceId() },
      groupedEvents: [{ events: batch, deviceInfo: {} }],
    },
    keepalive,
  );

let isFlushing = false;

/**
 * Uploads everything currently queued, dropping only the events the backend accepted so an
 * outage retries rather than loses them.
 *
 * @param keepalive Let the browser finish the requests after the page goes away
 * @returns Whether every batch was accepted
 */
const proceedEvents = async (keepalive = false): Promise<boolean> => {
  if (isFlushing) {
    return true;
  }

  const events = getStoredEvents();

  if (!events.length || !shouldUpload()) {
    return true;
  }

  isFlushing = true;

  try {
    let allSucceeded = true;

    for (const batch of chunk(events)) {
      // sequential on purpose: batches only exist when we are already behind, and firing them
      // all at once against a struggling backend is how you turn a blip into an outage
      // oxlint-disable-next-line eslint/no-await-in-loop -- sequential is the point, see above
      const succeeded = await sendBatch(batch, keepalive);

      if (succeeded) {
        removeStoredEvents(new Set(batch.map((event) => event.id)));
      } else {
        allSucceeded = false;
      }
    }

    return allSucceeded;
  } finally {
    isFlushing = false;
  }
};

/**
 * Starts the upload loop. Call once, at startup.
 *
 * A loop rather than recursion: this runs for the whole life of the tab, and a self-call would
 * keep every past iteration's promise alive.
 */
export const proceedEventsBatchingWithInterval = async (): Promise<never> => {
  let retries = 0;

  for (;;) {
    const startTime = Date.now();
    // oxlint-disable-next-line eslint/no-await-in-loop -- the loop is the schedule
    const succeeded = await proceedEvents();
    const elapsed = Date.now() - startTime;

    retries = succeeded || retries >= MAX_RETRIES ? 0 : retries + 1;
    const backoff = retries ** 2 * (BATCHING_INTERVAL / MAX_RETRIES);

    // oxlint-disable-next-line eslint/no-await-in-loop -- the loop is the schedule
    await delay(Math.max(0, BATCHING_INTERVAL - elapsed + backoff));
  }
};

/**
 * Flush on the way out. Without this the events leading up to a crash, a logout or a closed tab
 * — the ones we actually want when investigating — sit in localStorage until the next visit.
 */
export const registerAnalyticsFlushOnPageHide = (): (() => void) => {
  const flush = () => {
    void proceedEvents(true);
  };

  const onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      flush();
    }
  };

  window.addEventListener('pagehide', flush);
  document.addEventListener('visibilitychange', onVisibilityChange);

  return () => {
    window.removeEventListener('pagehide', flush);
    document.removeEventListener('visibilitychange', onVisibilityChange);
  };
};
